import Honeybadger from '@honeybadger-io/js';
import { z } from 'zod';
import reportExtractionFailure from './reportExtractionFailure';

// `common/json/extract` expressed as Zod schemas, with the same runtime
// behaviour to the value: a field the schema cannot read is replaced by the
// coercion of whatever arrived -- `String(undefined)` is the literal
// `"undefined"`, `Number(undefined)` is `NaN` -- the row still renders, the
// response never fails, and every substitution in one response is reported
// together.
//
// What Zod adds over the extractors is the static type. `common/json/extract`
// is Flow with no declarations, so under TypeScript every combinator resolves
// to `any` and checks nothing; these infer the type they produce.
//
// A boundary reading a response with these makes a deliberate choice to render
// a value it could not read rather than to refuse the response: refusing costs
// the reader the whole screen, and no response is worth that. ADR 0018 applies
// that one policy to every response a boundary reads -- a write's answer and a
// `react_component` prop payload included.
//
// What keeps a manufactured value off the screen is correct nullability, not a
// second policy. `nullable` and `withDefault` below intercept null and an
// absent key before the inner reader runs, so neither is a failure and neither
// is reported. A field the server can omit, declared required, is the one thing
// that manufactures `"undefined"`, `NaN` and `0`.

// Every field handed to `object()` has to absorb its own failures, so build each
// one from the combinators below rather than from a bare Zod schema. A field
// that refuses does not merely refuse: the error escapes past every `.catch()`
// around it, out of a reader whose whole contract is that it never throws. The
// tests pin that for a bare enum and a bare unknown.

// Mirrors extract/failure.js: substitutions inside one root parse collect here
// and are reported once, rather than one report per field.
const failures: string[] = [];
let collecting = false;

function substitute<T>(message: string, fallback: () => T): T {
  if (!collecting) {
    // The parse was not wrapped in `withExtractionReport`. Same fallback as the
    // extractors': surface it immediately in development, report it in
    // production, and degrade either way.
    const error = new Error(message);
    if (__DEV__) {
      throw error;
    }
    Honeybadger.notify(error);
    return fallback();
  }

  failures.push(message);
  return fallback();
}

// What the extractors call the input in a failure message.
function describe(input: unknown): string {
  return input === null ? 'null' : typeof input;
}

// Wraps a root parse so the substitutions inside it are reported as one
// extraction failure carrying every message and the body they came from.
export function withExtractionReport<T>(
  schema: z.ZodType<T>
): (json: unknown) => T {
  return json => {
    // Nesting hands the inner call the outer's collected messages: the inner
    // `finally` empties the collector, reports them against the wrong body, and
    // leaves `collecting` off for everything the outer parse has still to read.
    // Thrown before the collector is touched, so an inner call cannot disturb
    // the outer one, and a throw is right here because nesting is a fault in how
    // the schemas were wired rather than in the body being read.
    if (collecting) {
      throw new Error(
        'withExtractionReport cannot be nested; compose the schema instead'
      );
    }

    collecting = true;
    try {
      return schema.parse(json);
    } finally {
      // The collector is emptied before anything is reported, because reporting
      // can throw -- `Honeybadger.notify` in production, and the jest mock on
      // any call a test did not expect. Reporting first would leave `collecting`
      // armed and the messages in place, and the next read of any body would
      // then report them again as if they were its own.
      //
      // Reported from `finally` at all so a throw does not take the
      // substitutions with it. Every schema here absorbs its own failures, so a
      // throw means the schema is built wrong rather than the body being odd,
      // and what substituted before it is what says where.
      const collected = failures.splice(0);
      collecting = false;

      if (collected.length > 0) {
        reportExtractionFailure(collected, json);
      }
    }
  };
}

export const string: z.ZodType<string> = z
  .string()
  .catch(ctx =>
    substitute(`Expecting string, got ${describe(ctx.value)}`, () =>
      String(ctx.value)
    )
  );

export const number: z.ZodType<number> = z
  .number()
  .catch(ctx =>
    substitute(`Expecting number, got ${describe(ctx.value)}`, () =>
      Number(ctx.value)
    )
  );

export const boolean: z.ZodType<boolean> = z
  .boolean()
  .catch(ctx =>
    substitute(`Expecting boolean, got ${describe(ctx.value)}`, () =>
      Boolean(ctx.value)
    )
  );

// A Rails primary key: a number on the wire, a string in the domain. Reading a
// number is not a failure and is not reported.
export const id: z.ZodType<string> = z
  .union([z.string(), z.number()])
  .transform(String)
  .catch(ctx =>
    substitute(
      `Expecting id (number or string), got ${describe(ctx.value)}`,
      () => String(ctx.value)
    )
  );

// Null and an absent key both read as null, and neither is a failure. Anything
// else goes to the wrapped schema and substitutes there.
//
// The guard has to run before the wrapped schema, which is why this reads the
// input rather than composing `.nullable()` onto it: every schema here absorbs
// its own failures, so a wrapped one would have already substituted
// `"undefined"` for an absent key before a `.nullable()` outside it saw
// anything.
export function nullable<T>(inner: z.ZodType<T>): z.ZodType<T | null> {
  const read = (input: unknown): T | null =>
    input === null || input === undefined ? null : inner.parse(input);

  // `.optional()` is what lets an absent key reach `read` at all: without it
  // `object()` treats the key as required and refuses the field before this
  // runs. It is load-bearing rather than decorative.
  const schema = z.unknown().optional().transform(read);

  return schema as z.ZodType<T | null>;
}

// Null and an absent key both read as the given value, and neither is a
// failure. Anything else goes to the wrapped schema and substitutes there.
export function withDefault<T>(
  inner: z.ZodType<T>,
  defaultValue: T
): z.ZodType<T> {
  const read = (input: unknown): T =>
    input === null || input === undefined ? defaultValue : inner.parse(input);

  // `.optional()` for the same reason as `nullable` above.
  const schema = z.unknown().optional().transform(read);

  return schema as z.ZodType<T>;
}

// Rails renders a `decimal` column as either a number or a string depending on
// the serializer, so both are read and neither is a failure -- the wire format
// for money and every other decimal column.
//
// A string that is not numeric is a substitution rather than a silent `NaN`:
// `Number('abc')` is what the caller gets either way, but a total rendering as
// `NaN` is indistinguishable from a bug in the sum unless something says the
// field arrived unreadable. `z.number()` admits neither `NaN` nor `Infinity`,
// so the pipe needs no further refinement.
//
// The empty string is the exception, and reads as zero rather than as
// unreadable: `Number('')` is `0`, which `z.number()` accepts, so a blank goes
// down the success path and reports nothing. A blank fee is no charge, which is
// what zero says.
export const decimal: z.ZodType<number> = z
  .union([z.number(), z.string().transform(Number).pipe(z.number())])
  .catch(ctx =>
    substitute(
      `Expecting decimal (number or string), got ${describe(ctx.value)}`,
      () => Number(ctx.value)
    )
  );

// A value outside the vocabulary reads as its first member, which is
// `extract/oneOf`'s behaviour. There is no coercion for an enum, so unlike
// every combinator above the substitution is a choice rather than a cast: the
// first member is what the screen renders, and the report is the only thing
// that says the wire carried something else. Order the values so the first is
// the one a screen can survive being wrong about.
export function oneOf<const Values extends readonly [string, ...string[]]>(
  values: Values
): z.ZodType<Values[number]> {
  return z
    .enum(values)
    .catch(ctx =>
      substitute(
        `Expecting one of [${values.join(', ')}], got ${String(ctx.value)}`,
        () => values[0]
      )
    ) as z.ZodType<Values[number]>;
}

// The three ways an input fails to be an object, worded as the extractors word
// them.
function notAnObject(input: unknown): string {
  if (!input) {
    return 'Expecting an object, got a falsy value instead';
  }
  if (Array.isArray(input)) {
    return 'Expecting an object, got an array instead';
  }
  return `Expecting an object, got ${typeof input} instead`;
}

// An input that is not an object reads as an object with every field absent,
// so each field substitutes in turn -- the extractors' `extract({})`.
export function object<Shape extends z.ZodRawShape>(
  shape: Shape
): z.ZodType<z.infer<z.ZodObject<Shape>>> {
  const fields = z.object(shape);

  return fields.catch(ctx =>
    substitute(notAnObject(ctx.value), () => fields.parse({}))
  );
}

// Every element is read; none is dropped. An input that is not an array reads
// as an empty one.
export function array<T>(inner: z.ZodType<T>): z.ZodType<T[]> {
  return z
    .array(inner)
    .catch(ctx =>
      substitute(
        `Expecting an array, got ${describe(ctx.value)} instead`,
        () => []
      )
    );
}
